import deepmerge from 'deepmerge'

import { Theme, ThemeOptions } from '@mui/material'

import { fontFamily } from './getTheme'
import { scrollbarStyleDefaultHidden } from './scrollbarStyle'

const fontSize = 16
const htmlFontSize = 16

// Generate the application theme based on the white label theme
export const pxToRem = (size: number) => `${size / htmlFontSize}rem`

export const getDesignTokens = (whiteLabelThemeOptions: ThemeOptions) => {
  return (deepmerge({
    layout: {
      navHeaderHeight: pxToRem(56),
      defaultLeftDrawerWidth: pxToRem(220),
      defaultRightDrawerWidth: pxToRem(260),
      notificationSidebarWidth: pxToRem(480),

      navSidebarWidth: pxToRem(230),
      navSidebarCollapsedWidth: pxToRem(56),
      navSidebarTransitionSpeed: 250,

      widgetToolbarHeight: pxToRem(48),

      scrollbarWidth: '16px',
    },
    typography: {
      fontSize,
      htmlFontSize,
      pxToRem: (size: number) => pxToRem(size),
      spacing: (factor: number) => pxToRem(factor * 10),
      h1: {
        fontSize: pxToRem(24),
        fontWeight: 400,
        color: whiteLabelThemeOptions.palette?.text?.primary,
      },
      h2: {
        fontSize: pxToRem(14),
        fontWeight: 500,
        color: whiteLabelThemeOptions.palette?.text?.primary,
        textTransform: 'uppercase',
        fontFeatureSettings: '\'ss05\'',
      },
      h3: {
        color: whiteLabelThemeOptions.palette?.text?.primary,
      },
      h4: {
        color: whiteLabelThemeOptions.palette?.text?.primary,
      },
      h5: {
        color: whiteLabelThemeOptions.palette?.text?.primary,
      },
      body1: {
        fontSize: pxToRem(13),
        color: whiteLabelThemeOptions.palette?.text?.primary,
        fontWeight: 400,
        letterSpacing: '0.03em',
      },
      body2: {
        fontSize: pxToRem(13),
        color: whiteLabelThemeOptions.palette?.text?.primary,
        fontWeight: 500,
        letterSpacing: '0.03em',
      },
      subtitle2: {
        color: whiteLabelThemeOptions.palette?.text?.primary,
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
  }, whiteLabelThemeOptions))
}


export const getComponentOverrides = (theme: Theme) => ({
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: pxToRem(12),
        fontWeight: 600,
        position: 'relative',
        marginBottom: pxToRem(3),
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        paddingLeft: `${theme.typography.pxToRem(8)} !important`,
        paddingRight: `${theme.typography.pxToRem(8)} !important`,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        background: theme.palette.background.paper,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        fontFamily: fontFamily,
        borderRadius: pxToRem(6),
        boxShadow: '0 5px 30px rgba(0, 0, 0, 0.15)',
      },
    },
  },
  TableHead: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        fontSize: pxToRem(14),
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        position: 'relative',

        '& .MuiTabs-scroller': {
          overflowX: 'auto !important',
          ...scrollbarStyleDefaultHidden,
        },
      },
      scroller: {
        overflowX: 'auto !important',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          backgroundColor: theme.palette.divider,
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        color: theme.palette.text.dim,
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(14),
        textTransform: 'none',
        
        '&:before': {
          content: '""',
          display: 'block',
          position: 'absolute',
          top: 'calc(50% - 3px)',
          left: '6px',
          width: '6px',
          height: '6px',
          borderRadius: '100%',
          backgroundColor: theme.palette.primary.main,
          opacity: 0,
          transition: 'opacity 0.2s',
        },

        '&.Mui-selected': {
          color: theme.palette.text.primary,
          
          '&:before': {
            opacity: 1,
          },
        },

      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      text: {
        fontSize: theme.typography.pxToRem(14),
        textTransform: 'capitalize',
        letterSpacing: '0.04rem',
        borderRadius: theme.typography.pxToRem(4),
        fontWeight: 500,
        color: theme.palette.text.primary,
        backgroundColor: 'unset',
        position: 'relative', // Required for absolute positioning of pseudo-element
        
        '& .MuiButton-startIcon': {
          marginRight: theme.typography.pxToRem(4),
        },
        
        '&:hover': {
          color: theme.palette.text.primary,
          backgroundColor: 'unset',
        },
        
        '&:active': {
          color: theme.palette.text.primary,
          backgroundColor: 'unset',
        },
        
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          left: 0,
          width: '100%',
          height: '100%',
          borderRadius: theme.typography.pxToRem(4),
          backgroundColor: theme.palette.action.hover,
          opacity: 0,
          transform: 'scaleX(1) scaleY(1) perspective(1px)',
          transition: 'transform 0.1s cubic-bezier(0.35, 0.12, 0.14, 1.4), opacity 0.1s ease',
        },
        
        '&:hover::after': {
          opacity: 1,
          backgroundColor: theme.palette.action.hover,
          transform: 'scaleX(1) scaleY(1) perspective(1px)',
        },
        
        '&:active::after': {
          transform: 'scaleX(0.95) scaleY(0.95) perspective(1px)',
        },
      },
    },
  },
})

export const theme = getDesignTokens({})